// Pure functions extracted from KnowledgeBaseView for testability.

const TODAY = new Date().toISOString().slice(0, 10);

export function nodeLabel(n) {
  return n?.data?.label || n?.data?.title || (n?.type || 'node').replace('Node', '');
}

export function fieldType(f) {
  if (f.type === 'select' && Array.isArray(f.options) && f.options.length) {
    return `select: ${f.options.join(' | ')}`;
  }
  return f.type || 'text';
}

export function describeTrigger(trigger, nodes) {
  const d = trigger.data || {};
  const col = nodes.find(n => n.id === d.triggerCollectionId);
  const fn = nodes.find(n => n.id === d.triggerFunctionId);
  switch (d.triggerType) {
    case 'record_added':        return `Fires when a record is added to "${col ? nodeLabel(col) : 'a collection'}".`;
    case 'field_change':        return `Fires when a field changes in "${col ? nodeLabel(col) : 'a collection'}"${d.triggerValue ? ` (to "${d.triggerValue}")` : ''}.`;
    case 'function_task_change': return `Fires when a task changes in "${fn ? nodeLabel(fn) : 'a function'}"${d.triggerTaskValue ? ` (to "${d.triggerTaskValue}")` : ''}.`;
    case 'schedule':            return `Runs on a schedule${d.scheduleInterval ? ` (every ${d.scheduleInterval}s)` : ''}.`;
    case 'manual':              return 'Runs manually when started from the canvas.';
    case 'poll_api':            return 'Runs by polling an external API.';
    case 'webhook':             return 'Runs when an external webhook calls in.';
    default:                    return 'Starts an automated workflow.';
  }
}

export function findNode(nodes, id) { return id ? nodes.find(n => n.id === id) : undefined; }
export function outEdges(edges, sourceId) { return edges.filter(e => e.source === sourceId); }
export function branchTarget(out, handle, fallbackIdx) {
  return (out.find(x => x.sourceHandle === handle) || out[fallbackIdx])?.target;
}

export function describeFlow(trigger, nodes, edges) {
  const steps = [];
  const visited = new Set();
  const queue = [trigger.id];
  while (queue.length && steps.length < 40) {
    const currentId = queue.shift();
    if (!currentId || visited.has(currentId)) continue;
    visited.add(currentId);
    const node = findNode(nodes, currentId);
    if (!node) continue;
    const out = outEdges(edges, currentId);
    let line = `${nodeLabel(node)} (${node.type.replace('Node', '')})`;
    if (node.type === 'conditionNode') {
      const yes = findNode(nodes, branchTarget(out, 'yes', 0));
      const no = findNode(nodes, branchTarget(out, 'no', 1));
      line += ` — YES → ${yes ? nodeLabel(yes) : 'end'}, NO → ${no ? nodeLabel(no) : 'end'}`;
    } else if (node.type === 'actionNode') {
      line += `${node.data?.actionType ? ` — ${node.data.actionType.replace(/_/g, ' ')}` : ''}`;
    }
    steps.push(line);
    for (const e of out) {
      const target = findNode(nodes, e.target);
      if (target && target.type !== 'collectionNode' && !visited.has(e.target)) {
        queue.push(e.target);
      }
    }
  }
  return steps;
}

export function buildArticles(nodes, edges) {
  const collections = nodes.filter(n => n.type === 'collectionNode');
  const functions   = nodes.filter(n => n.type === 'functionNode');
  const connections = nodes.filter(n => n.type === 'connectionNode');
  const triggers    = nodes.filter(n => n.type === 'triggerNode');
  const actions     = nodes.filter(n => n.type === 'actionNode');
  const conditions  = nodes.filter(n => n.type === 'conditionNode');
  const gates       = nodes.filter(n => n.type === 'gateNode');

  if (!nodes.length) {
    return [{
      id: 'empty', category: 'overview', source: 'auto',
      title: 'Nothing to document yet', updatedBy: 'Projectly', updatedAt: TODAY,
      body: `Your Knowledge Base is **empty right now** — there's no pipeline in this workspace to document yet.\n\nIt writes itself: as soon as you add collections, functions, connections and automations on the canvas, articles describing how everything is wired will appear here automatically — no writing required.\n\n**To get started:**\n- Open the Pipeline and add a Collection\n- Wire a Trigger and an Action\n- Come back here to see it documented`,
    }];
  }

  const articles = [];

  articles.push({
    id: 'overview', category: 'overview', source: 'auto',
    title: 'Workspace overview', updatedBy: 'Projectly', updatedAt: TODAY,
    body: [
      `This workspace is documented automatically from its pipeline.`,
      ``,
      `**At a glance:**`,
      `- ${collections.length} data collection${collections.length === 1 ? '' : 's'}`,
      `- ${triggers.length} automation${triggers.length === 1 ? '' : 's'}` +
        (triggers.length
          ? ` — ${triggers.length} trigger${triggers.length === 1 ? '' : 's'}, ${actions.length} action${actions.length === 1 ? '' : 's'}` +
            (conditions.length ? `, ${conditions.length} condition${conditions.length === 1 ? '' : 's'}` : '') +
            (gates.length ? `, ${gates.length} approval gate${gates.length === 1 ? '' : 's'}` : '')
          : ''),
      `- ${functions.length} function${functions.length === 1 ? '' : 's'} (agent teams)`,
      `- ${connections.length} external connection${connections.length === 1 ? '' : 's'}`,
      ``,
      `Each section below is generated from what is actually on the canvas, so this stays current as the pipeline changes.`,
    ].join('\n'),
  });

  collections.forEach(col => {
    const fields = col.data?.fields || [];
    const recordCount = col._recordCount || 0;
    const usedByTriggers = triggers.filter(t => t.data?.triggerCollectionId === col.id).map(nodeLabel);
    const usedByActions = nodes.filter(n =>
      n.type === 'actionNode' &&
      (n.data?.collectionId === col.id || edges.some(e => e.source === n.id && e.target === col.id))
    ).map(nodeLabel);

    const lines = [
      `The **${nodeLabel(col)}** collection stores ${recordCount} record${recordCount === 1 ? '' : 's'}.`,
      ``,
      `**Fields:**`,
      ...(fields.length ? fields.map(f => `- ${f.name} (${fieldType(f)})`) : ['- No fields defined yet']),
      ``,
      `**Used by automations:**`,
    ];
    if (usedByTriggers.length) lines.push(`- Triggers: ${usedByTriggers.join(', ')}`);
    if (usedByActions.length)  lines.push(`- Actions: ${usedByActions.join(', ')}`);
    if (!usedByTriggers.length && !usedByActions.length) lines.push(`- Not yet referenced by any trigger or action`);

    articles.push({
      id: `col-${col.id}`, category: 'collections', source: 'auto',
      title: `Collection: ${nodeLabel(col)}`, updatedBy: 'Projectly', updatedAt: TODAY,
      body: lines.join('\n'),
    });
  });

  functions.forEach(fn => {
    const agents = fn.data?.agents || [];
    const taskFields = fn.data?.taskFields || [];
    const lines = [
      `The **${nodeLabel(fn)}** function${fn.data?.description ? ` — ${fn.data.description}` : ''} runs a team of ${agents.length} agent${agents.length === 1 ? '' : 's'}.`,
      ``,
      `**Team:**`,
      ...(agents.length
        ? agents.map(a => `- ${a.name}${a.isManager ? ' — manager' : ''}${a.role ? ` (${a.role})` : ''}`)
        : ['- No agents configured yet']),
      ``,
      `**Task fields:**`,
      ...(taskFields.length ? taskFields.map(f => `- ${f.name} (${fieldType(f)})`) : ['- No task fields defined yet']),
    ];
    if (fn.data?.playbook) { lines.push('', '**Playbook:**', fn.data.playbook); }
    articles.push({
      id: `fn-${fn.id}`, category: 'functions', source: 'auto',
      title: `Function: ${nodeLabel(fn)}`, updatedBy: 'Projectly', updatedAt: TODAY,
      body: lines.join('\n'),
    });
  });

  triggers.forEach(trigger => {
    const flow = describeFlow(trigger, nodes, edges);
    const lines = [
      describeTrigger(trigger, nodes),
      ``,
      `**Flow:**`,
      ...flow.map((step, i) => `${i + 1}. ${step}`),
    ];
    articles.push({
      id: `flow-${trigger.id}`, category: 'automations', source: 'auto',
      title: `Automation: ${nodeLabel(trigger)}`, updatedBy: 'Projectly', updatedAt: TODAY,
      body: lines.join('\n'),
    });
  });

  if (connections.length) {
    const lines = [
      `This workspace connects to ${connections.length} external service${connections.length === 1 ? '' : 's'}.`,
      ``,
      ...connections.map(c => {
        const d = c.data || {};
        const auth = d.authType && d.authType !== 'none' ? d.authType : 'no auth';
        return `- **${nodeLabel(c)}** — ${d.serviceType || 'custom'} (${auth})${d.serviceUrl ? `, ${d.serviceUrl}` : ''}`;
      }),
    ];
    articles.push({
      id: 'integrations', category: 'integrations', source: 'auto',
      title: 'Connected integrations', updatedBy: 'Projectly', updatedAt: TODAY,
      body: lines.join('\n'),
    });
  }

  return articles;
}
